import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';
import { mediaQueryLarge, getVisibleElements, center, closest } from '@theme/utilities';

/**
 * Group slider component for mobile devices
 * Enables horizontal scrolling for group.liquid block products on mobile viewports (< 749px)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller - The scrollable container element
 * @property {HTMLElement[]} [items] - The individual group items/cards
 *
 * @extends {Component<Refs>}
 */
class GroupSlider extends Component {
  requiredRefs = ['scroller'];

  /**
   * The Scroller instance that manages scrolling behavior
   * @type {Scroller | null}
   */
  #scroll = null;

  /**
   * Abort controller for event listeners
   * @type {AbortController}
   */
  #controller = new AbortController();

  /**
   * Current active item index
   * @type {number}
   */
  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    console.log('[GroupSlider] Connected', {
      enabled: this.#isEnabled(),
      isMobile: this.#isMobileViewport(),
      hasScroller: !!this.refs.scroller,
      dataset: this.dataset
    });

    // Only initialize if the setting is enabled
    if (!this.#isEnabled()) {
      console.log('[GroupSlider] Not enabled, skipping initialization');
      return;
    }

    // Initialize slider on mobile viewport
    if (this.#isMobileViewport()) {
      console.log('[GroupSlider] Initializing slider for mobile');
      this.#initSlider();
    } else {
      console.log('[GroupSlider] Desktop viewport, slider not initialized');
    }

    // Listen for viewport changes
    mediaQueryLarge.addEventListener('change', this.#handleViewportChange, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#destroySlider();
  }

  /**
   * Check if mobile slider is enabled via data attribute
   * @returns {boolean}
   */
  #isEnabled() {
    return this.dataset.enableMobileSlider === 'true';
  }

  /**
   * Check if current viewport is mobile (< 749px)
   * @returns {boolean}
   */
  #isMobileViewport() {
    return !mediaQueryLarge.matches;
  }

  /**
   * Initialize the slider with Scroller instance
   */
  #initSlider() {
    if (this.#scroll) {
      console.log('[GroupSlider] Already initialized');
      return; // Already initialized
    }

    const { scroller } = this.refs;
    if (!scroller) {
      console.error('[GroupSlider] No scroller ref found!', this.refs);
      return;
    }

    console.log('[GroupSlider] Initializing Scroller', {
      scroller,
      children: this.items.length
    });

    // Add mobile slider attribute for CSS targeting
    this.setAttribute('mobile-slider-active', '');

    // Set item count for CSS (to hide arrows when only 1 item)
    this.setAttribute('data-item-count', this.items.length.toString());

    // Initialize Scroller with callbacks
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#handleScrollStart,
      onScrollEnd: this.#handleScrollEnd,
    });

    // Set initial snap behavior
    this.#scroll.snap = true;

    console.log('[GroupSlider] Scroller initialized, snap enabled');

    // Sync initial state
    this.#sync();
  }

  /**
   * Destroy the slider and clean up
   */
  #destroySlider() {
    if (!this.#scroll) return;

    this.#scroll.destroy();
    this.#scroll = null;

    // Remove mobile slider attributes
    this.removeAttribute('mobile-slider-active');
    this.removeAttribute('data-item-count');
  }

  /**
   * Handle viewport size changes
   * @param {MediaQueryListEvent} event
   */
  #handleViewportChange = (event) => {
    if (event.matches) {
      // Switched to desktop (>= 750px)
      this.#destroySlider();
    } else {
      // Switched to mobile (< 749px)
      if (this.#isEnabled()) {
        this.#initSlider();
      }
    }
  };

  /**
   * Handle scroll events - update current index
   */
  #handleScroll = () => {
    this.#sync();
  };

  /**
   * Handle scroll start - visual feedback
   */
  #handleScrollStart = () => {
    this.setAttribute('scrolling', '');
  };

  /**
   * Handle scroll end - cleanup
   */
  #handleScrollEnd = () => {
    this.removeAttribute('scrolling');
  };

  /**
   * Sync the current visible item index
   * @returns {number} The index of the current item
   */
  #sync() {
    const items = this.items;
    if (!items || !items.length) return 0;

    const { scroller } = this.refs;
    if (!scroller) return 0;

    // Get visible items (at least 50% visible)
    const visibleItems = getVisibleElements(scroller, items, 0.5, 'x');

    if (!visibleItems.length) return this.#currentIndex;

    // Find the item closest to the center of the scroller
    const centers = visibleItems.map((item) => center(item, 'x'));
    const scrollerCenter = center(scroller, 'x');
    const closestCenter = closest(centers, scrollerCenter);
    const closestItem = visibleItems[centers.indexOf(closestCenter)];

    if (!closestItem) return this.#currentIndex;

    const newIndex = items.indexOf(closestItem);

    if (newIndex !== this.#currentIndex) {
      const oldIndex = this.#currentIndex;
      this.#currentIndex = newIndex;
      console.log('[GroupSlider] Index changed:', oldIndex, '->', newIndex);
      this.#updateIndicators();
    }

    return this.#currentIndex;
  }

  /**
   * Update any visual indicators (optional - for future pagination dots)
   */
  #updateIndicators() {
    // Dispatch custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('group-slider:change', {
        bubbles: true,
        detail: {
          index: this.#currentIndex,
          total: this.items?.length || 0,
        },
      })
    );
  }

  /**
   * Get the list of items/cards in the slider
   * Auto-detects direct children of the scroller element
   * @returns {HTMLElement[]}
   */
  get items() {
    // First try to get items from refs (if manually added)
    if (this.refs.items?.length) {
      return this.refs.items;
    }

    // Fallback: get all direct children of scroller
    const { scroller } = this.refs;
    if (!scroller) return [];

    return /** @type {HTMLElement[]} */ (
      Array.from(scroller.children).filter(
        (child) => child instanceof HTMLElement && !child.hasAttribute('aria-hidden')
      )
    );
  }

  /**
   * Get the current active item index
   * @returns {number}
   */
  get currentIndex() {
    return this.#currentIndex;
  }

  /**
   * Check if slider is currently active
   * @returns {boolean}
   */
  get isActive() {
    return this.#scroll !== null;
  }

  /**
   * Navigate to the previous item
   * @param {Event} [event] - The event that triggered the navigation
   */
  previous(event) {
    event?.preventDefault();
    if (!this.#scroll || !this.items.length) return;

    // Sync current index first to get accurate position
    this.#sync();

    const targetIndex = Math.max(0, this.#currentIndex - 1);
    const targetItem = this.items[targetIndex];

    if (targetItem) {
      console.log('[GroupSlider] Navigate to previous:', this.#currentIndex, '->', targetIndex);
      this.#scroll.to(targetItem);
    }
  }

  /**
   * Navigate to the next item
   * @param {Event} [event] - The event that triggered the navigation
   */
  next(event) {
    event?.preventDefault();
    if (!this.#scroll || !this.items.length) return;

    // Sync current index first to get accurate position
    this.#sync();

    const targetIndex = Math.min(this.items.length - 1, this.#currentIndex + 1);
    const targetItem = this.items[targetIndex];

    if (targetItem) {
      console.log('[GroupSlider] Navigate to next:', this.#currentIndex, '->', targetIndex);
      this.#scroll.to(targetItem);
    }
  }
}

if (!customElements.get('group-slider')) {
  customElements.define('group-slider', GroupSlider);
}
